import { useState, useEffect } from 'react';
import { getSuppliers, getSupplierFiles, createIncident } from '../../services/supportService';

const CATEGORIES = ['Report File Issue', 'Request Backup', 'New User Creation'];

const ALLOWED_EXTENSIONS = [
  '.xls', '.xlsx', '.pdf', '.csv',
  '.png', '.jpg', '.jpeg',
  '.doc', '.docx', '.ppt', '.pptx',
];
const MAX_FILE_SIZE = 49 * 1024 * 1024;

function getFileExt(name) {
  const m = (name || '').match(/\.[^.]+$/);
  return m ? m[0].toLowerCase() : '';
}

export default function ReportIncidentModal({ show, onClose, onCreated, userEmail, msalInstance }) {
  const [category, setCategory] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [docRef, setDocRef] = useState('');
  const [newUserText, setNewUserText] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState('');

  const [suppliers, setSuppliers] = useState([]);
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const [suppliersError, setSuppliersError] = useState('');

  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesError, setFilesError] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Reset form when modal opens/closes
  useEffect(() => {
    if (show) {
      setCategory('');
      setSupplierId('');
      setSupplierName('');
      setDocRef('');
      setNewUserText('');
      setNotes('');
      setFile(null);
      setFileError('');
      setSubmitError('');
      setFiles([]);
      setFilesError('');
    }
  }, [show]);

  // Load suppliers when category is "Report File Issue"
  useEffect(() => {
    if (category === 'Report File Issue' && suppliers.length === 0 && !suppliersLoading) {
      setSuppliersLoading(true);
      setSuppliersError('');
      getSuppliers(msalInstance)
        .then(data => setSuppliers(data))
        .catch(err => setSuppliersError(err.response?.data?.error || err.message))
        .finally(() => setSuppliersLoading(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  // Reset conditional Row-2 when category changes
  useEffect(() => {
    setSupplierId('');
    setSupplierName('');
    setDocRef('');
    setNewUserText('');
    setFiles([]);
    setFilesError('');
  }, [category]);

  // Load files when supplier changes
  useEffect(() => {
    if (!supplierId) {
      setFiles([]);
      setDocRef('');
      return;
    }
    setFilesLoading(true);
    setFilesError('');
    setDocRef('');
    getSupplierFiles(supplierId, msalInstance)
      .then(data => setFiles(data))
      .catch(err => setFilesError(err.response?.data?.error || err.message))
      .finally(() => setFilesLoading(false));
  }, [supplierId]);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    setFileError('');
    if (!f) { setFile(null); return; }

    const ext = getFileExt(f.name);
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      setFileError(`File type "${ext}" is not allowed. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`);
      setFile(null);
      e.target.value = '';
      return;
    }
    if (f.size > MAX_FILE_SIZE) {
      setFileError('File exceeds 49 MB limit.');
      setFile(null);
      e.target.value = '';
      return;
    }
    setFile(f);
  };

  const handleSubmit = async () => {
    if (!category) return;
    if (category === 'Report File Issue' && (!supplierId || !docRef)) return;

    setSubmitting(true);
    setSubmitError('');

    try {
      const fd = new FormData();
      fd.append('requester_email', userEmail);
      fd.append('category', category);

      if (category === 'Report File Issue') {
        fd.append('supplier_id', supplierId);
        fd.append('supplier_name', supplierName);
        fd.append('document_name_reference', docRef);
      } else if (category === 'New User Creation') {
        fd.append('new_user_text', newUserText);
      }

      if (notes) fd.append('notes', notes);
      if (file) fd.append('attachment', file);

      const result = await createIncident(fd, msalInstance);

      if (result.emailError) {
        alert(`Incident created but email notification failed: ${result.emailError}`);
      }

      onCreated();
    } catch (err) {
      setSubmitError(err.response?.data?.error || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!show) return null;

  const isValid = category
    && (category !== 'Report File Issue' || (supplierId && docRef))
    && !fileError;

  return (
    <div className="modal-backdrop-fo" onClick={onClose}>
      <div className="modal-fo" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <button className="mcl" onClick={onClose}>&#10005;</button>
        <div className="m-title">New Support Incident</div>
        <div className="m-sub">Submit a support request. All fields marked with * are required.</div>

        {/* Row 1: Category */}
        <div className="mb-3">
          <label className="flabel">Category *</label>
          <select className="finput" value={category} onChange={e => setCategory(e.target.value)}>
            <option value="">Select category...</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {/* Row 2: Conditional */}
        {category === 'Report File Issue' && (
          <div className="row g-3 mb-3">
            <div className="col-sm-6">
              <label className="flabel">Supplier *</label>
              {suppliersLoading ? (
                <div style={{ fontSize: 12, color: 'var(--t3)', padding: '8px 0' }}>Loading suppliers...</div>
              ) : suppliersError ? (
                <div style={{ fontSize: 12, color: 'var(--red)' }}>{suppliersError}</div>
              ) : (
                <select
                  className="finput"
                  value={supplierId}
                  onChange={e => {
                    const id = e.target.value;
                    setSupplierId(id);
                    const s = suppliers.find(s => String(s.supplier_id) === id);
                    setSupplierName(s ? s.supplier_name : '');
                  }}
                >
                  <option value="">Select supplier...</option>
                  {suppliers.map(s => (
                    <option key={s.supplier_id} value={s.supplier_id}>{s.supplier_name}</option>
                  ))}
                </select>
              )}
            </div>
            <div className="col-sm-6">
              <label className="flabel">File *</label>
              {!supplierId ? (
                <div style={{ fontSize: 12, color: 'var(--t3)', padding: '8px 0' }}>Select a supplier first</div>
              ) : filesLoading ? (
                <div style={{ fontSize: 12, color: 'var(--t3)', padding: '8px 0' }}>Loading files...</div>
              ) : filesError ? (
                <div style={{ fontSize: 12, color: 'var(--red)' }}>{filesError}</div>
              ) : files.length === 0 ? (
                <div style={{ fontSize: 12, color: 'var(--t3)', padding: '8px 0' }}>No files found for this supplier</div>
              ) : (
                <select className="finput" value={docRef} onChange={e => setDocRef(e.target.value)}>
                  <option value="">Select file...</option>
                  {files.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              )}
            </div>
          </div>
        )}

        {category === 'New User Creation' && (
          <div className="mb-3">
            <label className="flabel">New User Details</label>
            <input
              className="finput"
              placeholder="e.g. John Smith, john.smith@company.com"
              value={newUserText}
              onChange={e => setNewUserText(e.target.value)}
            />
          </div>
        )}

        {/* Row 3: Notes + File */}
        {category && (
          <>
            <div className="mb-3">
              <label className="flabel">Notes</label>
              <textarea
                className="finput"
                rows={3}
                placeholder="Describe the issue or request..."
                value={notes}
                onChange={e => setNotes(e.target.value)}
                style={{ resize: 'vertical' }}
              />
            </div>
            <div className="mb-3">
              <label className="flabel">Attachment</label>
              <input
                type="file"
                className="finput"
                onChange={handleFileChange}
                accept={ALLOWED_EXTENSIONS.join(',')}
              />
              {fileError && <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 4 }}>{fileError}</div>}
              <div style={{ fontSize: 11, color: 'var(--t3)', marginTop: 4 }}>
                Allowed: XLS, XLSX, PDF, CSV, PNG, JPG, JPEG, DOC, DOCX, PPT, PPTX. Max 49 MB.
              </div>
            </div>
          </>
        )}

        {submitError && (
          <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 12 }}>{submitError}</div>
        )}

        {/* Row 4: Buttons */}
        <div className="d-flex justify-content-end gap-2 pt-3 border-top">
          <button className="btn btn-s" onClick={onClose} disabled={submitting}>Cancel</button>
          <button className="btn btn-p" onClick={handleSubmit} disabled={!isValid || submitting}>
            {submitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
}
